import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { nowLocalDate } from '@utils';

const datePicker = cva([], {
  variants: {
    variant: {
      primary: [
        'grow',
        'block',
        'w-full',
        'rounded-md',
        'bg-white',
        'py-1.5',
        'pr-3',
        'pl-3',
        'text-base',
        'border-gray-200',
        'px-3',
        'py-1.5',
        'text-base',
        'text-gray-950',
        'outline-1',
        '-outline-offset-1',
        'outline-gray-200',
        'focus:outline-2',
        'focus:-outline-offset-2',
        'focus:outline-blue-200',
      ],
      invalid: [
        'grow',
        'block',
        'w-full',
        'rounded-md',
        'bg-white',
        'py-1.5',
        'pr-3',
        'pl-3',
        'text-base',
        'border-gray-200',
        'text-red-900',
        'outline-1',
        '-outline-offset-1',
        'outline-red-300',
        'focus:outline-2',
        'focus:-outline-offset-2',
        'focus:outline-red-600',
      ],
    },
    sizeVariant: {
      sm: ['text-xs/4'],
      md: ['sm:text-sm/6'],
      lg: [],
    },
    disabled: {
      true: [],
      false: [],
    },
  },
  defaultVariants: {
    variant: 'primary',
    sizeVariant: 'md',
    disabled: false,
  },
});

export interface DatePickerProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof datePicker> {
  label?: string;
  name: string;
  value: string;
  startDate?: string;
  endDate?: string;
  variant?: 'primary' | 'invalid';
  sizeVariant?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  isInvalid?: boolean;
  validationMessage?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function Datepicker({
  label,
  name,
  value = nowLocalDate(),
  startDate,
  endDate,
  variant = 'primary',
  sizeVariant = 'md',
  disabled,
  className,
  isInvalid = false,
  validationMessage = '',
  onChange,
  ...props
}: DatePickerProps) {
  const appliedVariant = isInvalid ? 'invalid' : variant;

  return (
    <div className='flex flex-col grow'>
      {label && (
        <label
          htmlFor={name}
          className='block text-sm font-medium leading-5 font-inter text-gray-900 mb-2'
        >
          {label}
        </label>
      )}
      <div className='relative'>
        <input
          id={name}
          name={name}
          type='date'
          value={value}
          onChange={onChange}
          min={startDate}
          max={endDate}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? `${name}-error` : undefined}
          className={twMerge(
            datePicker({ variant: appliedVariant, sizeVariant, disabled }),
            className,
            isInvalid && 'pr-10'
          )}
          {...props}
        />
        {isInvalid && (
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3'>
            <ExclamationCircleIcon
              aria-hidden='true'
              className='h-5 w-5 text-red-500'
            />
          </span>
        )}
      </div>
      {isInvalid && validationMessage && (
        <p id={`${name}-error`} className='mt-2 text-sm text-red-600'>
          {validationMessage}
        </p>
      )}
    </div>
  );
}
